import { useMemo, useState } from "react";
import { useSearchParams } from "react-router";
import { Plus, UsersRound } from "lucide-react";

import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { cn } from "@/lib/utils";
import { useMediaQuery } from "@/features/lead/hooks/use-media-query";
import { useCustomers, useCustomerTagCounts } from "@/features/customer/api";
import { useTags } from "@/features/tag/api";
import { CustomerSearchBar } from "@/features/customer/components/customer-search-bar";
import { CustomerListItem } from "@/features/customer/components/customer-list-item";
import { CustomerDetailPanel } from "@/features/customer/components/customer-detail-panel";
import { CustomerCreateSheet } from "@/features/customer/customer-create-sheet";
import type { Customer, CustomerListQuery, CustomerTagCounts } from "@/types/customer";
import type { Tag } from "@/features/tag/types";

// A generous page so the alphabetical grouping spans many letters at once
// (the list re-sorts the fetched page by name client-side).
const PAGE_SIZE = 50;

export default function CustomerPage() {
  const [searchParams, setSearchParams] = useSearchParams();
  const isDesktop = useMediaQuery("(min-width: 1024px)");
  const [createOpen, setCreateOpen] = useState(false);

  const tagsQuery = useTags();
  const tags = useMemo(() => tagsQuery.data ?? [], [tagsQuery.data]);

  const search = searchParams.get("search") ?? "";
  const activeTagId = useMemo<number | null>(() => {
    const raw = searchParams.get("tagId");
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }, [searchParams]);
  const activeTag = tags.find((t) => t.id === activeTagId) ?? null;

  const query = useMemo<CustomerListQuery>(() => {
    const base: CustomerListQuery = { page: 0, size: PAGE_SIZE };
    if (search !== "") base.search = search;
    if (activeTagId !== null) base.tagId = activeTagId;
    return base;
  }, [search, activeTagId]);

  const customersQuery = useCustomers(query);
  const tagCountsQuery = useCustomerTagCounts();

  const selectedId = useMemo<number | null>(() => {
    const raw = searchParams.get("selected");
    if (raw === null) return null;
    const n = Number.parseInt(raw, 10);
    return Number.isNaN(n) ? null : n;
  }, [searchParams]);

  const patchParam = (key: string, value: string | null) => {
    const next = new URLSearchParams(searchParams);
    if (value === null || value === "") next.delete(key);
    else next.set(key, value);
    setSearchParams(next, { replace: true });
  };

  const total = tagCountsQuery.data?.total ?? customersQuery.data?.totalItems;

  const listColumn = (
    <CustomerListColumn
      customersQuery={customersQuery}
      tags={tags}
      tagCounts={tagCountsQuery.data}
      activeTagId={activeTagId}
      onTagChange={(id) => patchParam("tagId", id === null ? null : String(id))}
      selectedId={selectedId}
      onSelect={(c) => patchParam("selected", String(c.id))}
      hasFilter={search !== "" || activeTagId !== null}
    />
  );

  // Mobile: a selected customer takes over the screen.
  if (!isDesktop && selectedId !== null) {
    return (
      <div className="h-[calc(100svh-var(--app-bottom-nav,0px))]">
        <CustomerDetailPanel
          customerId={selectedId}
          onClose={() => patchParam("selected", null)}
        />
      </div>
    );
  }

  return (
    <div className="flex flex-col gap-5 px-4 py-6 sm:px-6 sm:py-7">
      <header className="flex flex-col gap-3 sm:flex-row sm:items-end sm:justify-between">
        <div className="flex flex-col gap-1">
          <p className="font-mono text-[11px] uppercase tracking-wider text-ink-faint">
            Danh bạ {activeTag ? `· ${activeTag.name}` : "· Tất cả"}
          </p>
          <h1 className="text-2xl font-bold tracking-tight sm:text-[28px]">
            Khách hàng
            {total !== undefined ? (
              <span className="ml-2 font-mono text-base font-normal text-muted-foreground">
                {total}
              </span>
            ) : null}
          </h1>
        </div>
        <div className="flex items-center gap-2">
          <div className="hidden sm:block sm:w-72">
            <CustomerSearchBar />
          </div>
          <Button
            onClick={() => setCreateOpen(true)}
            className="hidden sm:inline-flex"
            aria-label="Thêm khách hàng"
          >
            <Plus aria-hidden="true" />
            Thêm khách hàng
          </Button>
        </div>
      </header>

      {isDesktop ? (
        <div className="grid h-[calc(100svh-200px)] min-h-[480px] grid-cols-[360px_1fr] overflow-hidden rounded-xl border border-border bg-card shadow-[var(--shadow-soft)]">
          <div className="flex flex-col border-r border-border">{listColumn}</div>
          <div className="bg-background">
            <CustomerDetailPanel customerId={selectedId} />
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-3">
          <CustomerSearchBar />
          <div className="overflow-hidden rounded-xl border border-border bg-card">
            {listColumn}
          </div>
        </div>
      )}

      {/* Mobile FAB */}
      <Button
        onClick={() => setCreateOpen(true)}
        size="icon"
        aria-label="Thêm khách hàng"
        className="fixed bottom-24 right-5 z-30 size-14 rounded-full shadow-lg sm:hidden"
      >
        <Plus aria-hidden="true" className="size-6" />
      </Button>

      <CustomerCreateSheet open={createOpen} onClose={() => setCreateOpen(false)} />
    </div>
  );
}

interface ListColumnProps {
  customersQuery: ReturnType<typeof useCustomers>;
  tags: Tag[];
  tagCounts: CustomerTagCounts | undefined;
  activeTagId: number | null;
  onTagChange: (tagId: number | null) => void;
  selectedId: number | null;
  onSelect: (customer: Customer) => void;
  hasFilter: boolean;
}

function CustomerListColumn({
  customersQuery,
  tags,
  tagCounts,
  activeTagId,
  onTagChange,
  selectedId,
  onSelect,
  hasFilter,
}: ListColumnProps) {
  const groups = useMemo(
    () => groupByLetter(customersQuery.data?.items ?? []),
    [customersQuery.data],
  );

  return (
    <div className="flex h-full flex-col">
      {/* Segment chips — "Tất cả" + one per org tag */}
      <div className="flex flex-wrap gap-1.5 border-b border-border bg-muted/30 p-3">
        <SegmentChip
          label="Tất cả"
          count={tagCounts?.total}
          active={activeTagId === null}
          onClick={() => onTagChange(null)}
        />
        {tags.map((t) => (
          <SegmentChip
            key={t.id}
            label={t.name}
            count={tagCounts?.byTagId[t.id] ?? 0}
            active={activeTagId === t.id}
            onClick={() => onTagChange(t.id)}
          />
        ))}
      </div>

      <div className="flex-1 overflow-y-auto">
        {customersQuery.isPending ? (
          <ListSkeleton />
        ) : customersQuery.isError ? (
          <div className="p-6 text-sm text-muted-foreground">
            Không thể tải danh sách
          </div>
        ) : groups.length === 0 ? (
          <div className="flex flex-col items-center gap-2 p-8 text-center">
            <UsersRound className="size-8 text-ink-faint" />
            <p className="text-sm font-semibold">
              {hasFilter ? "Không có khách hàng phù hợp" : "Chưa có khách hàng"}
            </p>
          </div>
        ) : (
          groups.map((g) => (
            <div key={g.letter}>
              <div className="sticky top-0 z-10 flex items-center gap-2 bg-muted/60 px-4 py-1.5 backdrop-blur">
                <span className="font-mono text-[11px] font-semibold text-ink-soft">
                  {g.letter}
                </span>
                <span className="font-mono text-[11px] uppercase tracking-wider text-ink-faint">
                  · {g.items.length} khách
                </span>
              </div>
              {g.items.map((c) => (
                <CustomerListItem
                  key={c.id}
                  customer={c}
                  selected={selectedId === c.id}
                  onSelect={onSelect}
                />
              ))}
            </div>
          ))
        )}
      </div>
    </div>
  );
}

function SegmentChip({
  label,
  count,
  active,
  onClick,
}: {
  label: string;
  count: number | undefined;
  active: boolean;
  onClick: () => void;
}) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-pressed={active}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-2.5 py-1 text-[12px] font-medium transition-colors",
        active
          ? "bg-foreground text-background"
          : "bg-background text-ink-soft hover:bg-muted",
      )}
    >
      {label}
      {count !== undefined ? (
        <span
          className={cn(
            "font-mono text-[11px]",
            active ? "text-background/70" : "text-ink-faint",
          )}
        >
          {count}
        </span>
      ) : null}
    </button>
  );
}

function ListSkeleton() {
  return (
    <div aria-busy="true" aria-label="Đang tải danh sách">
      {Array.from({ length: 7 }).map((_, i) => (
        <div key={i} className="flex items-center gap-3 border-b border-border p-3">
          <Skeleton className="size-9 rounded-lg" />
          <div className="flex flex-1 flex-col gap-1">
            <Skeleton className="h-3.5 w-2/3" />
            <Skeleton className="h-3 w-1/2" />
          </div>
        </div>
      ))}
    </div>
  );
}

interface LetterGroup {
  letter: string;
  items: Customer[];
}

/** Sort the fetched page by name (vi locale) and group by first letter. */
function groupByLetter(customers: Customer[]): LetterGroup[] {
  const sorted = [...customers].sort((a, b) =>
    a.fullName.localeCompare(b.fullName, "vi"),
  );
  const groups: LetterGroup[] = [];
  for (const c of sorted) {
    const letter = (c.fullName.trim()[0] ?? "#").toUpperCase();
    const last = groups[groups.length - 1];
    if (last !== undefined && last.letter === letter) {
      last.items.push(c);
    } else {
      groups.push({ letter, items: [c] });
    }
  }
  return groups;
}
